import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { useEffect, useRef } from "react";
import { ChatMessageRow } from "~/components/common/chat/messages/ChatMessageRow";
import type { Deal } from "~/models/Deal";
import type { Message, MessageReceiver } from "~/models/Message";
import { postRead as postReadForClient } from "~/utils/api/deals/Message";
import { postRead as postReadForVendor } from "~/utils/api/vendors/deals/Message";
import Styles from "./ChatMessageArea.module.scss";

type Props = DeepReadonly<{
	deal: Deal;
	handleMutate: () => Promise<void>;
	isOpen: boolean;
	messages: Message[];
	receiver: MessageReceiver;
}>;

export function ChatMessageArea({
	isOpen,
	deal,
	messages,
	receiver,
	handleMutate
}: Props) {
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isOpen) return;

		const timer = setTimeout(async () => {
			const readMethod =
				receiver === "maintenance_company"
					? postReadForClient
					: postReadForVendor;
			if (
				deal.messageSummaryUnread?.unread ||
				deal.messageSummaryUnread?.displayUnread
			) {
				await readMethod(deal.id);
			}
			await handleMutate();
		}, 2000);
		return () => {
			clearTimeout(timer);
		};
	}, [deal, handleMutate, isOpen, receiver]);

	useEffect(() => {
		void handleMutate();
		if (containerRef.current !== null) {
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
		}
	}, [handleMutate, messages.length]);

	return (
		<div className={Styles.Messages} ref={containerRef}>
			{messages.map((message) => (
				<ChatMessageRow
					deal={deal}
					key={message.id}
					message={message}
					receiver={receiver}
				/>
			))}
		</div>
	);
}
